<!-- src/components/forms/completed/LiteracyTestCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- 1. Assessment Identity -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Assessment Identity</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label text-[#20FCF1]">Sentinel (PTS) Number</span>
          <span class="value tracking-[0.2em]">{{ submissionData.sentinelNumber || 'N/A' }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Date of Competency Test</span>
          <span class="value">{{ formatDate(submissionData.dateCompleted || submissionData.submittedAt, 'dateOnly') }}</span>
        </div>
      </div>
    </section>

    <!-- 2. Phonetic Results Grid -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <LanguageIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Phonetic Transcription Results</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="(answer, key) in phoneticAnswers" :key="key" 
             class="flex flex-col p-6 bg-white border border-slate-200 rounded-[24px] shadow-sm relative overflow-hidden">
          <div class="absolute top-0 right-0 p-4">
            <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]/30" />
          </div>
          <span class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2">{{ answer.label }}</span>
          <p class="text-xs font-black text-[#0A1630] uppercase tracking-wider leading-relaxed">
            {{ submissionData[answer.key] }}
          </p>
        </div>
      </div>
    </section>

    <!-- 3. Final Attestation -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Communication Standards Verified</p>
          </div>
          <div class="p-6 bg-slate-900 rounded-[32px] border border-slate-800 max-w-sm">
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Official Result</p>
            <p class="text-xs text-slate-300 font-medium italic">"The candidate has successfully demonstrated the ability to transcribe and communicate using the standard phonetic alphabet protocols."</p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[260px]">
          <p class="text-[9px] font-black uppercase text-slate-400 mb-4">Authorized Signature Seal</p>
          <img :src="submissionData.signatureDataUrl || submissionData.signatureUrl" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex flex-col items-center">
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">{{ submissionData.name }}</span>
            <span class="text-[9px] font-bold text-slate-400 mt-1 uppercase">{{ formatDate(submissionData.submittedAt) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { 
  IdentificationIcon, LanguageIcon, CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true }
});

const phoneticAnswers = [
  { label: 'A. DAVID', key: 'answerDavid' },
  { label: 'B. JOHN', key: 'answerJohn' },
  { label: 'C. LONDON', key: 'answerLondon' },
  { label: 'D. 976', key: 'answer976' },
  { label: 'E. 10', key: 'answer10' }
];
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>